import React, { useCallback, useContext, useEffect, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { UrlParamTypes } from "../../../../types/org-display";
import { GetUsersListParams, getOrgUserList } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

// Constants
const SEARCH_DEBOUNCE_DELAY_MS = 300;
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_OFFSET = 0;
const CONTAINER_MIN_WIDTH = 350;
const ALERT_MARGIN = "2px";
const MIN_REASON_LENGTH = 10; // Minimum characters required for reason
const SUPPORT_USER_DELETION_TIME = "2 hours";

interface ProvisionSupportUserProps {
  orgId: string;
  userEmail: string;
  handleSelectedUserId: (userId: string) => void;
  handleDisableConfirmBtn: (isDisabled: boolean) => void;
  handleReasonChange: (reason: string) => void;
}

const ProvisionSupportUser = ({
  orgId,
  userEmail,
  handleSelectedUserId,
  handleDisableConfirmBtn,
  handleReasonChange,
}: ProvisionSupportUserProps) => {
  const { regionCode } = useParams<UrlParamTypes>();
  const [orgUsersList, setOrgUsersList] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [reason, setReason] = useState("");

  const { showError } = useContext(SnackbarContext);

  const fetchOrgUsers = useCallback(
    async (query = "") => {
      setIsLoading(true);
      const payload: GetUsersListParams = {
        organizationId: orgId,
        pageSize: DEFAULT_PAGE_SIZE,
        offset: DEFAULT_OFFSET,
        sortDirection: SortDirection.ASC,
        sortType: UserSortType.FIRST_NAME,
        query: query,
        userStatus: UserStatus.UNSPECIFIED,
        adminOnly: false, // Allow selection of all users, not just admins
      };

      try {
        const res = await getOrgUserList(
          payload,
          callOptionsHeaders(regionCode, orgId),
        );
        setOrgUsersList(res.users);
      } catch (error) {
        showError(error as GrpcError);
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, regionCode, showError],
  );

  const debouncedFetchOrgUsers = useCallback(
    (query: string) => {
      // Clear any existing timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      // Trim the query here to capture the correct value
      const trimmedQuery = query.trim();

      // Set a new timer
      const newTimer = setTimeout(() => {
        fetchOrgUsers(trimmedQuery);
      }, SEARCH_DEBOUNCE_DELAY_MS);

      setSearchDebounceTimer(newTimer);
    },
    [searchDebounceTimer, fetchOrgUsers],
  );

  const handleSetSelectedUserId = (value: string | User | null) => {
    if (!value) {
      return;
    }
    if (typeof value === "string") {
      return setSelectedUserId(value);
    }
    if (value && typeof value === "object") {
      return setSelectedUserId(value.id);
    }
  };

  useEffect(() => {
    handleSelectedUserId(selectedUserId);
    // handleSelectedUserId function not memoized in parent, so may have unintended side effects to include in deps
    // eslint-disable-next-line
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId && reason.trim().length > MIN_REASON_LENGTH) {
      handleDisableConfirmBtn(false);
    } else {
      handleDisableConfirmBtn(true);
    }
    // eslint-disable-next-line
  }, [selectedUserId, reason]);

  useEffect(() => {
    fetchOrgUsers();
    // eslint-disable-next-line
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  useEffect(() => {
    handleReasonChange(reason);
    // eslint-disable-next-line
  }, [reason]);

  return (
    <Grid
      container
      direction="row"
      spacing={1}
      style={{ minWidth: CONTAINER_MIN_WIDTH }}
    >
      <Grid item xs={12}>
        <Alert
          severity="info"
          variant="filled"
          style={{ margin: ALERT_MARGIN }}
        >
          <Box>
            <Typography component="p" sx={{ mb: 1 }}>
              This will provision you (<b>{userEmail}</b>) as a support user
              into the organisation. Once added, you can use the org switcher in
              the app to go into the org. The provisioned user will be deleted{" "}
              <b>{SUPPORT_USER_DELETION_TIME}</b> after it is provisioned.
            </Typography>
            <Typography
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <InfoIcon fontSize="small" />
              <a
                href="https://safetyculture.atlassian.net/wiki/x/8IADCQE"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Support User Provisioning & Access (SUPA)
              </a>
            </Typography>
          </Box>
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" gutterBottom>
          Select a user to assign documents created by the support user:
        </Typography>
        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
          <Typography component="p" variant="body2">
            This <b>does not</b> impersonate the selected user, and the support
            user <b>does not</b> inherit their permissions or permission sets.
            The support user is provisioned with its own access.
          </Typography>
          <Typography component="p" variant="body2" sx={{ mt: 1 }}>
            The selected user only becomes the owner of documents the support
            user creates, so that work is not lost when the support user is
            deleted.
          </Typography>
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          autoHighlight
          autoComplete
          options={orgUsersList}
          loading={isLoading}
          onChange={(_event, value, reason) => {
            if (reason === "clear") {
              setSelectedUserId("");
            }
            if (reason === "selectOption" && value) {
              handleSetSelectedUserId(value);
            }
          }}
          onInputChange={(_event, value) => {
            // Use debounced search
            debouncedFetchOrgUsers(value);
          }}
          getOptionLabel={(option: User) =>
            `${option.firstName} ${option.lastName} - ${option.email}`
          }
          renderOption={(props, option) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
            const { key, ...otherProps } = props;
            return (
              <li key={key} {...otherProps}>
                <Box>
                  <Typography>
                    {option.firstName} {option.lastName}
                  </Typography>
                  <Typography variant="caption">{option.email}</Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Select User"
              name="selectedUser"
              variant="outlined"
              margin="normal"
              inputProps={{
                ...params.inputProps,
                "data-testid": "autocomplete-user-field",
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" gutterBottom>
          Reason for provisioning support user:
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          label="Reason"
          name="reason"
          variant="outlined"
          margin="normal"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. customer support ticket, debugging issue, etc."
          helperText={`${reason.length}/${MIN_REASON_LENGTH} characters minimum required`}
          error={reason.length > 0 && reason.trim().length <= MIN_REASON_LENGTH}
          inputProps={{
            "data-testid": "reason-text-field",
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ProvisionSupportUser;
